import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'

import config from '@/config'
import lang from '@/lang'
import CheckIcon from '@assets/images/icons/check.svg'
import BaseDrawer from '@/components/drawers/base-drawer'
import expenseConfig from '@/modules/expense/config'
import invoiceConfig from '@/modules/invoice/config'
import IconZoomMinus from '@assets/images/icons/zoom_minus.svg'
import IconZoomPlus from '@assets/images/icons/zoom_plus.svg'

import request from '@/modules/base/util/request'
import { transactionTypes, payKinds, assignmentTypes, RECEIPT_PAYMENT_TYPES } from '@/modules/base/util/constants'
import { fetchBankAccountList, openBankAccountAddModal } from '@/redux/ducks/banking/bankAccountList'
import { CancelButton, DocumentControlButton } from '@/modules/base/components/buttons/index'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'

import { toast } from '@/modules/base/components/toast/toastConfig'
import DocumentDetailPreview from '@/modules/document/components/edit/document-detail-preview'
import BaseDrawerPanelHeader from '@/components/drawers/base-drawer-panel-header/base-drawer-panel-header'
import ExpenseAndRevenueDetailPreview from '@/modules/expense-revenue/components/drawer/expense-and-revenue-detail-preview'
import BankTransferForm from '@/modules/banking/components/bank-transfer-v2/bank-transfer-form'
import moment from 'moment'
import { BookingService } from '@/modules/banking/services/bookingService'
import ExpenseService from '@/modules/expense/ExpenseService'
import pollTransaction from '@/redux/ducks/banking/_helpers/pollTransaction'
import { CreditNoteService } from '@/modules/credit-note/credit-note-service'
import OnlineBankingModal, { showBankAccountModal } from '@/modules/base/components/modal/online-banking-modal/online-banking-modal'
import { generateProcessId } from '@/modules/banking/_helpers/generateProcessId'
import { ManualCreditNoteService } from '@/modules/manualCreditNote/ManualCreditNoteService'
import navigateToCurrentPage from '@/utils/navigateToCurrentPage'
import { useLocation, useNavigate } from 'react-router'

const { dateFormat: { api: apiDateFormat } } = config
const { BANK } = payKinds

const {
  TRANSACTION_TYPE_CREDIT_NOTE,
  TRANSACTION_TYPE_EXPENSE,
  TRANSACTION_TYPE_REVENUE,
  TRANSACTION_TYPE_INVOICE,
  TRANSACTION_TYPE_MANUAL_CREDIT_NOTE,
} = transactionTypes

const { receiptBaseUrl } = expenseConfig

const useStyles = makeStyles((theme) => ({
  previewHeaderDocumentDetailPreview: {
    height: 80,
    backgroundColor: theme.superlightGrey,
  },
  detailContainer: {
    maxWidth: 'calc(100vw - 350px - 100px)',
    width: '750px',
    height: '100%',
  },
  mainContainer: {
    width: '400px',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    '& > *': {
      margin: '20px 0px 20px 20px',
    },
    '& a': {
      border: theme.border,
      backgroundColor: theme.white,
      minWidth: 45,
      minHeight: 45,
    },
    '& a:hover': {
      backgroundColor: theme.backgroundColor,
    },
  },
  button: {
    height: 40,
    maxHeight: 40,
    width: 40,
    maxWidth: 40,
    transition: '.2s',
  },
  secondViewWrapper: {
    height: '100%',
    width: 'auto',
  },
  drawerFooter: {
    '& > :first-child': {
      width: 150,
    },
    '& > *': {
      width: 215,
    },
  },
}))

const BankTransferDrawer = (props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    open,
    transaction,
    withoutPdf,
    onClose,
    onConfirm,
    onAdd,
  } = props

  const type = (
    [TRANSACTION_TYPE_EXPENSE, TRANSACTION_TYPE_REVENUE].includes(transaction.type)
      ? transaction.type
      : transaction.type.includes(TRANSACTION_TYPE_MANUAL_CREDIT_NOTE)
        ? TRANSACTION_TYPE_MANUAL_CREDIT_NOTE
        : transaction.type.includes(TRANSACTION_TYPE_CREDIT_NOTE)
          ? TRANSACTION_TYPE_CREDIT_NOTE
          : TRANSACTION_TYPE_INVOICE
  )

  const {
    bankAccountList: { bankAccounts: allBankAccounts },
  } = useSelector(state => state.banking)
  const dispatch = useDispatch()

  const [pdfUrl, setPdfUrl] = useState('')
  const [openBankingModal, setOpenBankingModal] = useState(false)
  const [previewZoom, setPreviewZoom] = useState(1)
  const [files, setFiles] = useState([])

  const classes = useStyles()

  const [openDrawer, setOpenDrawer] = useState(false)

  const zoom = (zoomStep) => {
    setPreviewZoom(Math.max(previewZoom + zoomStep, 0.25))
  }

  useEffect(() => {
    if (!withoutPdf && ![TRANSACTION_TYPE_EXPENSE, TRANSACTION_TYPE_REVENUE].includes(type)) {
      request(`${type === TRANSACTION_TYPE_CREDIT_NOTE ? invoiceConfig.cancellationUrl : invoiceConfig.resourceUrl}/preview/${transaction.id}`, {
        isConcurrent: true,
        auth: true,
        method: 'GET',
      }).then(response => {
        const { pdf: buffer } = response.body.data
        const blob = new Blob([Buffer.from(buffer)])

        const blobUrl = window.URL.createObjectURL(blob)
        setPdfUrl(currentPdfUrl => {
          window.URL.revokeObjectURL(currentPdfUrl)
          return blobUrl
        })
      }).catch((e) => {
        console.error(e)
        return toast.showToast({ type: 'error', message: lang.transactionEdit.transactionPreview.createPreviewErrorMessage })
      })
    } else if (!withoutPdf && [TRANSACTION_TYPE_EXPENSE, TRANSACTION_TYPE_REVENUE].includes(type)) {
      const receiptData = transaction.receipts.map(receipt => {
        return {
          ...receipt,
          url: `${receiptBaseUrl}${receipt.url}`,
        }
      })
      setFiles(receiptData)
    }

    if (allBankAccounts.length === 0) {
      dispatch(fetchBankAccountList()).then((bankAccounts) => {
        if (showBankAccountModal(bankAccounts)) {
          setOpenBankingModal(true)
        } else {
          setOpenDrawer(open)
        }
      })
    }

    return () => {
      if (!withoutPdf && ![TRANSACTION_TYPE_EXPENSE, TRANSACTION_TYPE_REVENUE].includes(type)) {
        window.URL.revokeObjectURL(pdfUrl)
      }
    }
  }, [])

  useEffect(() => {
    if (allBankAccounts.length > 0) {
      if (showBankAccountModal(allBankAccounts)) {
        setOpenBankingModal(true)
      } else {
        setOpenDrawer(open)
      }
    }
  }, [open])

  const [formSubmitting, setFormSubmitting] = useState(false)
  const bankTransferFormData = useSelector(state => state.banking.bankTransfer.data)

  const handleCancelClick = () => {
    onClose && onClose()
  }
  const createBankTransfers = (booking) => {
    const { id } = transaction

    return new Promise((resolve, reject) => {
      const newBankTransferBookings = [booking]

      if (!newBankTransferBookings.length) return resolve(transaction)

      // when defined, called instead of sending requests
      if (onAdd) {
        const sanitizedBookings = newBankTransferBookings
          .map((booking) => ({ ...booking, bookingType: RECEIPT_PAYMENT_TYPES.BANK_TRANSFER }))
        return onAdd(sanitizedBookings)
      }

      const processIds = []
      const newBankTransferPromises = newBankTransferBookings.map(booking => {
        const processId = generateProcessId()
        processIds.push(processId)
        const data = type === TRANSACTION_TYPE_EXPENSE
          ? ExpenseService.createBankTransferRequestData(booking)
          : type === TRANSACTION_TYPE_MANUAL_CREDIT_NOTE
            ? ManualCreditNoteService.createBankTransferRequestData(booking)
            : CreditNoteService.createBankTransferRequestData(booking)
        return type === TRANSACTION_TYPE_EXPENSE
          ? BookingService.bankTransfer.createByExpenseId(id, data, processId)
          : type === TRANSACTION_TYPE_MANUAL_CREDIT_NOTE
            ? BookingService.bankTransfer.createByManualCreditNoteId(id, data, processId)
            : BookingService.bankTransfer.createByCreditNoteId(id, data, processId)
      })

      Promise.all(newBankTransferPromises)
        .then((responses) => {
          const transactions = responses.map(({ body: { data } }) => data)
          const transactionPromises = transactions.map((transaction, index) => pollTransaction(transaction, 'bookingApi', processIds[index]))
          return Promise.all(transactionPromises)
        })
        .then(() => {
          return type === TRANSACTION_TYPE_EXPENSE
            ? ExpenseService.syncState(id)
            : type === TRANSACTION_TYPE_MANUAL_CREDIT_NOTE
              ? ManualCreditNoteService.syncState(id)
              : CreditNoteService.syncState(id)
        })
        .then(() => {
          resolve()
          toast.showToast({ message: lang.payments.bankTransferDrawer.successMessage })
          onConfirm?.() || onClose?.()
          navigateToCurrentPage(navigate, location)
        })
        .catch(error => {
          toast.showToast({ error })
          type === TRANSACTION_TYPE_EXPENSE
            ? ExpenseService.syncState(id)
              .finally(() => {
                navigate('/expenses')
                reject(error)
              })
            : type === TRANSACTION_TYPE_MANUAL_CREDIT_NOTE
              ? ManualCreditNoteService.syncState(id)
                .finally(() => {
                  navigate('/invoices')
                  reject(error)
                })
              : CreditNoteService.syncState(id)
                .finally(() => {
                  navigate('/invoices')
                  reject(error)
                })
        })
    })
  }
  const handleSubmitClick = () => {
    if (formSubmitting) { return false }

    const { bankAccount } = bankTransferFormData
    const {
      id: bankAccountId,
      accountName,
    } = bankAccount
    const { accountHolder, usage: purposeDescription, iban, bic, amount: bookingAmount } = bankTransferFormData

    const newBooking = {
      id: undefined,
      iban,
      bic,
      type: BANK,
      bookingDate: moment(bankTransferFormData.executionDate).format(apiDateFormat),
      isNew: true,
      bankAccountId,
      bankAccountName: accountName,
      bankAccount,
      bookingAmount,
      accountHolder,
      purposeDescription,
      assignments: [
        { assignmentType: type === TRANSACTION_TYPE_EXPENSE ? assignmentTypes.EXPENSE : assignmentTypes.INVOICE, amount: bookingAmount },
      ],
    }

    createBankTransfers(newBooking)
    setFormSubmitting(true)
  }

  const isFormValid = bankTransferFormData.validate({ skipBic: true })

  const actions = [
    <CancelButton
      type='cancel'
      label={lang.cancelButtonLabel}
      onClick={handleCancelClick} />,
    <ButtonIconSquare
      customClass={classes.submitButton}
      type='submit'
      icon={CheckIcon}
      disabled={!isFormValid || formSubmitting}
      tooltip={!isFormValid ? lang.payments.bankTransferDrawer.disabledSubmitTooltip : ''}
      label={onAdd ? lang.payments.bankTransferDrawer.confirmButton : lang.payments.bankTransferDrawer.submitButton}
      onClick={handleSubmitClick} />,
  ]

  const drawerSecondViewHeader = !withoutPdf && ![TRANSACTION_TYPE_EXPENSE, TRANSACTION_TYPE_REVENUE].includes(type)
    ? (
      <div className={classes.previewHeader}>
        <DocumentControlButton
          icon={IconZoomMinus}
          title={lang.expense.previewToolbar.items.zoomOut}
          customClass={classes.button}
          onClick={() => zoom(-0.1)} />
        <DocumentControlButton
          icon={IconZoomPlus}
          title={lang.expense.previewToolbar.items.zoomIn}
          customClass={classes.button}
          onClick={() => zoom(+0.1)} />
      </div>
    )
    : null

  let secondView = null
  if (!withoutPdf) {
    switch (type) {
      case TRANSACTION_TYPE_EXPENSE:
      case TRANSACTION_TYPE_REVENUE:
        secondView = (<ExpenseAndRevenueDetailPreview
          key='bankTransferPdfPreview'
          classNamePreviewHeader={classes.previewHeaderDocumentDetailPreview}
          classNames={classes.detailContainer} file={files}
          onZoom={zoom} downloadDisabled={true}
          zoom={previewZoom} />)
        break

      default:
        secondView = (
          <>
            <BaseDrawerPanelHeader showCloseIcon={false} children={drawerSecondViewHeader} />
            {pdfUrl && <DocumentDetailPreview
              classNames={classes.detailContainer}
              disableActions
              file={{ url: pdfUrl, mimetype: 'application/pdf' }}
              onZoom={zoom}
              downloadLabel={lang.generics.download}
              downloadUrl={pdfUrl}
              zoom={previewZoom}
              hideControls
            />}
          </>
        )
        break
    }
  }

  const handleClose = () => {
    setOpenDrawer(false)
    onClose && onClose()
  }

  return (
    <>
      {!openBankingModal && (<BaseDrawer
        withFooterLine
        open={openDrawer}
        enableBackdropClick
        onClose={handleClose}
        secondView={secondView}
        classNameSecondView={classes.secondViewWrapper}
        headerChildren={lang.payments.bankTransferDrawer.title}
        footerChildren={actions}
        footerChildrenClass={classes.drawerFooter}
      >
        <div className={classes.mainContainer}>
          <BankTransferForm transaction={transaction} />
        </div>
      </BaseDrawer>)
      }
      <OnlineBankingModal
        open={openBankingModal}
        onConfirm={() => {
          dispatch(openBankAccountAddModal())
          navigate('/banking')
        }}
        onAbort={() => {
          setOpenBankingModal(false)
          handleClose()
        }}
      />
    </>
  )
}

BankTransferDrawer.propTypes = {
  open: PropTypes.bool,
  transaction: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
  withoutPdf: PropTypes.bool,
}

export default BankTransferDrawer
